import React, { useContext, useEffect, useState } from "react";
import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Checkbox } from "~/components/ui/checkbox";
import { Button } from "~/components/ui/button";
import Skeleton from "react-loading-skeleton";
import {
  useDeleteTrainedIndicatorMutation,
  useMlModelsLazyQuery,
} from "generated/hooks";
import { useUpdateTrainedIndicatorMutation } from "generated/hooks";
import { useLabIndicatorDefinitionsLazyQuery } from "generated/hooks";
import { useCreateTrainedIndicatorMutation } from "generated/hooks";
import { useFetchAll } from "~/hooks/useFetchAll";
import { useCopyTrainedIndicatorMutation } from "../../graphql/generated/hooks";
import { toast } from "sonner";
import { useCurrentUser } from "@modules/authentication/useCurrentUser";
import { useNavigate, useNavigation } from "react-router-dom";

interface Indicator {
  id: string;
  minValue: string;
  maxValue: string;
  inferenceModelName?: string | null;
  name: string;
}

const TrainedIndicatorsPage: React.FC = () => {
  const [fetchMlModels] = useMlModelsLazyQuery();
  const {
    data: allModels,
    loading: modelsLoading,
    error: modelsError,
    refetch: refetchModels,
  } = useFetchAll(fetchMlModels, (d) => d.mlModels, {
    variables: { after: null },
  });

  const [fetchLabIndicators] = useLabIndicatorDefinitionsLazyQuery();
  const { data: allLabIndicators } = useFetchAll(
    fetchLabIndicators,
    (d) => d.labIndicatorDefinitions,
    { variables: { after: null } },
  );
  const [newIndicator, setNewIndicator] = useState<{
    name: string;
    minValue: string;
    maxValue: string;
    inferenceModelName: string;
  } | null>(null);
  const currentUser = useCurrentUser();

  const [targetModelId, setTargetModelId] = useState<string>("");
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [selectedForCopy, setSelectedForCopy] = useState<Set<string>>(
    new Set()
  );
  const [selectedForDelete, setSelectedForDelete] = useState<Set<string>>(
    new Set()
  );
  const [editedIndicators, setEditedIndicators] = useState<
    Record<string, Indicator>
  >({});
  const [updateIndicator, { loading: saveLoading }] =
    useUpdateTrainedIndicatorMutation();

  const [createTrainedIndicator] = useCreateTrainedIndicatorMutation();

  const [copyTrainedIndicator, { loading: copyLoading }] =
    useCopyTrainedIndicatorMutation();

  const [deleteTrainedIndicator, { loading: deleteLoading }] =
    useDeleteTrainedIndicatorMutation();
  const nav = useNavigate();

  useEffect(() => {
    if (
      currentUser?.currentUser &&
      currentUser.currentUser.email !== "j.forrester@pestechnologies.com" &&
      currentUser.currentUser.email !== "g.smith@pestechnologies.com"
    ) {
      nav("/404");
    }
  }, [currentUser, nav]);

  useEffect(() => {
    if (!selectedModelId && allModels[0]) {
      setSelectedModelId(allModels[0].id);
    }
  }, [allModels, selectedModelId]);

  const currentModel = allModels.find(
    (model) => model?.id === selectedModelId
  );
  const indicators = currentModel?.trainedIndicators?.nodes ?? [];

  const handleModelChange = (value: string) => {
    setSelectedModelId(value ?? null);
    setEditedIndicators({});
  };

  const handleIndicatorChange = (
    indicatorId: string,
    field: keyof Indicator,
    value: string | number
  ) => {
    const indicator = indicators.find((ind) => ind.id === indicatorId);
    if (!indicator) {
      return;
    }

    setEditedIndicators((prev) => ({
      ...prev,
      [indicatorId]: {
        ...(prev[indicatorId] || indicator),
        [field]: value,
      },
    }));
  };

  const getAvailableIndicatorOptions = (currentIndicatorId: string) => {
    // Get the current indicator
    const currentIndicator = indicators.find(
      (ind) => ind.id === currentIndicatorId
    );

    // Get all currently used indicator names except the current one
    const usedNames = new Set(
      indicators
        .filter((ind) => ind.id !== currentIndicatorId)
        .map((ind) => ind.name)
    );

    // Get all lab indicator definitions
    const labIndicators = allLabIndicators;

    // Create options array starting with current indicator
    const options = currentIndicator
      ? [
          {
            value: currentIndicator.name,
            label: currentIndicator.name,
          },
        ]
      : [];

    // Add unused lab indicators
    return [
      ...options,
      ...labIndicators
        .filter(
          (labInd) =>
            !usedNames.has(labInd.name) &&
            labInd.name !== currentIndicator?.name
        )
        .map((labInd) => ({
          value: labInd.name,
          label: labInd.name,
        })),
    ];
  };
  const handleCopy = async () => {
    if (!targetModelId || selectedForCopy.size === 0) {
      return;
    }

    try {
      // Copy each indicator individually since backend only supports single copy
      const copyPromises = Array.from(selectedForCopy).map((trainedIndicatorId) =>
        copyTrainedIndicator({
          variables: {
            input: {
              trainedIndicatorId,
              targetModelId,
            },
          },
        })
      );
      await Promise.all(copyPromises);
      await refetchModels();
      setSelectedForCopy(new Set());
      setTargetModelId("");
      toast.success("Indicators copied successfully");
    } catch (error) {
      console.error("Failed to copy indicators:", error);
      toast.error(
        `Failed to copy indicators: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  };

  const handleSave = async () => {
    try {
      // Update each indicator individually since backend only supports single update
      const updatePromises = Object.values(editedIndicators).map((indicator) =>
        updateIndicator({
          variables: {
            input: {
              id: indicator.id,
              minValue: Number(indicator.minValue),
              maxValue: Number(indicator.maxValue),
              inferenceModelName: indicator.inferenceModelName?.trim() || null,
            },
          },
        })
      );
      await Promise.all(updatePromises);
      setEditedIndicators({});
      toast.success("Changes saved successfully");
    } catch (error) {
      console.error("Failed to update indicators:", error);
      toast.error(
        `Failed to save changes: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  };

  const handleDelete = async () => {
    if (selectedForDelete.size === 0) {
      return;
    }

    if (
      window.confirm("Are you sure you want to delete the selected indicators?")
    ) {
      try {
        // Delete each indicator individually since backend only supports single delete
        const deletePromises = Array.from(selectedForDelete).map((id) =>
          deleteTrainedIndicator({
            variables: { id },
          })
        );
        await Promise.all(deletePromises);
        setSelectedForDelete(new Set());
        toast.success("Indicators deleted successfully");
        await refetchModels();
      } catch (error) {
        console.error("Failed to delete indicators:", error);
        toast.error(
          `Failed to delete indicators: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    }
  };

  const handleCreateIndicator = async (data: {
    name: string;
    minValue: number;
    maxValue: number;
    inferenceModelName: string;
    indicatorDefinitionId: string;
  }) => {
    if (!selectedModelId) {
      return;
    }

    try {
      await createTrainedIndicator({
        variables: {
          input: {
            indicatorDefinitionId: data.indicatorDefinitionId,
            minValue: data.minValue,
            maxValue: data.maxValue,
            inferenceModelName: data.inferenceModelName.trim() || null,
            modelId: selectedModelId,
          },
        },
      });
      await refetchModels();
      setNewIndicator(null);
      toast.success("Indicator created successfully");
    } catch (error) {
      console.error("Failed to create indicator:", error);
      toast.error(
        `Failed to create indicator: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  };

  const handleAddIndicatorClick = () => {
    setNewIndicator({
      name: "",
      minValue: "0",
      maxValue: "0",
      inferenceModelName: "",
    });
  };

  if (modelsLoading) {
    return (
      <PageTemplate
        title="Trained Indicators"
        activeItem="Admin"
        activeSubItem="Trained Indicators"
      >
        <Card>
          <CardContent className="pt-6">
            <Skeleton
              height={30}
              width={200}
              style={{ marginBottom: "10px" }}
            />
            <Skeleton
              height={30}
              width={200}
              style={{ marginBottom: "10px" }}
            />
            <Skeleton height={30} width={200} />
          </CardContent>
        </Card>
      </PageTemplate>
    );
  }

  if (modelsError) {
    return (
      <PageTemplate title="Trained Indicators">
        <Card>
          <CardContent className="pt-6">
            <p>Oh No! Something went wrong :-(</p>
          </CardContent>
        </Card>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      title="Trained Indicators"
      breadcrumbs={[
        { label: "Admin", path: "/admin" },
        { label: "Trained Indicators", path: "/admin/trained-indicators" },
      ]}
      actions={[
        ...(Object.keys(editedIndicators).length > 0
          ? [
              {
                text: saveLoading ? "Saving..." : "Save Changes",
                action: handleSave,
              },
            ]
          : []),
        ...(selectedForCopy.size > 0
          ? [
              {
                text: copyLoading ? "Copying..." : "Copy Selected",
                action: handleCopy,
                disabled: !targetModelId,
              },
            ]
          : []),
        ...(selectedForDelete.size > 0
          ? [
              {
                text: deleteLoading ? "Deleting..." : "Delete Selected",
                action: handleDelete,
              },
            ]
          : []),
      ]}
      activeItem="Admin"
      activeSubItem="Trained Indicators"
    >
      <div className="flex w-full flex-col gap-2 mb-4">
        <Label htmlFor="model-select">Select Model:</Label>
        <Select value={selectedModelId ?? ""} onValueChange={handleModelChange}>
          <SelectTrigger id="model-select" className="w-full">
            <SelectValue placeholder="Select model" />
          </SelectTrigger>
          <SelectContent>
            {allModels.map((model) => (
              <SelectItem key={model?.id} value={model?.id ?? ""}>
                {model?.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedForCopy.size > 0 && (
        <div className="flex w-full flex-col gap-2 mb-4">
          <Label htmlFor="target-model">Copy to Model:</Label>
          <Select
            value={targetModelId}
            onValueChange={(value) => setTargetModelId(value)}
          >
            <SelectTrigger id="target-model" className="w-full">
              <SelectValue placeholder="Select target model" />
            </SelectTrigger>
            <SelectContent>
              {allModels
                .filter((model) => model?.id !== selectedModelId)
                .map((model) => (
                  <SelectItem key={model?.id} value={model?.id ?? ""}>
                    {model?.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex justify-end">
            <Button
              disabled={!selectedModelId}
              onClick={handleAddIndicatorClick}
              variant="default"
              size="default"
            >
              Add Indicator
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {newIndicator && (
            <div className="flex items-end gap-4 p-4 border rounded-md bg-muted/20">
              <div className="flex gap-2">
                <div style={{ width: "100px" }} />
              </div>
              <div className="flex w-full flex-col gap-2">
                <Label>Indicator</Label>
                <Select
                  value={newIndicator.name}
                  onValueChange={(value) =>
                    setNewIndicator((prev) =>
                      prev ? { ...prev, name: value } : null
                    )
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select indicator" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableIndicatorOptions("").map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex w-full flex-col gap-2">
                <Label>Min Value</Label>
                <Input
                  value={newIndicator.minValue.toString()}
                  onChange={(e) =>
                    setNewIndicator((prev) =>
                      prev ? { ...prev, minValue: e.target.value } : null
                    )
                  }
                />
              </div>
              <div className="flex w-full flex-col gap-2">
                <Label>Max Value</Label>
                <Input
                  value={newIndicator.maxValue.toString()}
                  onChange={(e) =>
                    setNewIndicator((prev) =>
                      prev ? { ...prev, maxValue: e.target.value } : null
                    )
                  }
                />
              </div>
              <div className="flex w-full flex-col gap-2">
                <Label>Inference Model Name</Label>
                <Input
                  value={newIndicator.inferenceModelName}
                  placeholder="20260213_090606"
                  onChange={(e) =>
                    setNewIndicator((prev) =>
                      prev
                        ? { ...prev, inferenceModelName: e.target.value }
                        : null
                    )
                  }
                />
              </div>
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  if (newIndicator.name && selectedModelId) {
                    handleCreateIndicator({
                      name: newIndicator.name,
                      minValue: parseFloat(newIndicator.minValue),
                      maxValue: parseFloat(newIndicator.maxValue),
                      inferenceModelName: newIndicator.inferenceModelName,
                      indicatorDefinitionId:
                        allLabIndicators.find(
                          (ind) => ind.name === newIndicator.name
                        )?.id || "",
                    });
                    setNewIndicator(null);
                  }
                }}
              >
                Save
              </Button>
            </div>
          )}
          {indicators.map((indicator) => {
            const edited = editedIndicators[indicator.id];
            return (
              <div key={indicator.id} className="flex items-end gap-4">
                <div className="flex gap-2 mb-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`copy-${indicator.id}`}
                      checked={selectedForCopy.has(indicator.id)}
                      onCheckedChange={(checked) => {
                        const newSelection = new Set(selectedForCopy);
                        if (checked) {
                          newSelection.add(indicator.id);
                        } else {
                          newSelection.delete(indicator.id);
                        }
                        setSelectedForCopy(newSelection);
                      }}
                    />
                    <Label htmlFor={`copy-${indicator.id}`}>Copy</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`delete-${indicator.id}`}
                      checked={selectedForDelete.has(indicator.id)}
                      onCheckedChange={(checked) => {
                        const newSelection = new Set(selectedForDelete);
                        if (checked) {
                          newSelection.add(indicator.id);
                        } else {
                          newSelection.delete(indicator.id);
                        }
                        setSelectedForDelete(newSelection);
                      }}
                      className="data-[state=checked]:bg-destructive data-[state=checked]:border-destructive"
                    />
                    <Label
                      htmlFor={`delete-${indicator.id}`}
                      className="text-destructive"
                    >
                      Delete
                    </Label>
                  </div>
                </div>
                <div className="flex w-full flex-col gap-2">
                  <Label>Indicator</Label>
                  <Select
                    value={edited?.name ?? indicator.name}
                    onValueChange={(value) =>
                      handleIndicatorChange(indicator.id, "name", value)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select indicator" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableIndicatorOptions(indicator.id).map(
                        (option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex w-full flex-col gap-2">
                  <Label>Min Value</Label>
                  <Input
                    value={
                      edited?.minValue?.toString() ??
                      indicator.minValue.toString()
                    }
                    onChange={(e) =>
                      handleIndicatorChange(
                        indicator.id,
                        "minValue",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div className="flex w-full flex-col gap-2">
                  <Label>Max Value</Label>
                  <Input
                    value={
                      edited?.maxValue?.toString() ??
                      indicator.maxValue.toString()
                    }
                    onChange={(e) =>
                      handleIndicatorChange(
                        indicator.id,
                        "maxValue",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div className="flex w-full flex-col gap-2">
                  <Label>Inference Model Name</Label>
                  <Input
                    value={
                      edited?.inferenceModelName ??
                      indicator.inferenceModelName ??
                      ""
                    }
                    placeholder="20260213_090606"
                    onChange={(e) =>
                      handleIndicatorChange(
                        indicator.id,
                        "inferenceModelName",
                        e.target.value
                      )
                    }
                  />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </PageTemplate>
  );
};

export default TrainedIndicatorsPage;
